import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import ReactGA from "react-ga";
import {
  FiCopy,
  FiCheck,
  FiShare2,
  FiMail,
  FiMessageSquare,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import { FaTelegramPlane, FaWhatsapp } from "react-icons/fa";
import { HiOutlineUser, HiOutlineLink } from "react-icons/hi";
import { IoTrendingUp } from "react-icons/io5";
import { BsQrCodeScan } from "react-icons/bs";
import logo from "../../assets/cashlet.svg";
import "./referral.css";

const CHOTTU_REFERRAL_BASE_URL = "https://go.cashlet.co.ke/referral";
const CHOTTU_REFERRAL_DISPLAY_URL = CHOTTU_REFERRAL_BASE_URL;
const HEAR_FROM_VALUE = "refferal";

const buildReferralLink = (code) => {
  const params = new URLSearchParams({
    hearFrom: HEAR_FROM_VALUE,
    refferalCode: code,
    _ch_incp: "1",
  });

  return `${CHOTTU_REFERRAL_BASE_URL}?${params.toString()}`;
};

const ReferralPage = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const codeFromUrl = queryParams.get("code");
  const [referralCode, setReferralCode] = useState(codeFromUrl || "");
  const [referralLink, setReferralLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const hasCodeFromUrl = Boolean(codeFromUrl);

  useEffect(() => {
    ReactGA.pageview(window.location.pathname + window.location.search);
  }, []);

  useEffect(() => {
    if (codeFromUrl) {
      setReferralLink(buildReferralLink(codeFromUrl));
      setSelectedPlatform(null);
      setCopied(false);
    }
  }, [codeFromUrl]);

  const handleGetLink = () => {
    if (referralCode.trim()) {
      setReferralLink(buildReferralLink(referralCode.trim()));
      setSelectedPlatform(null);
      setCopied(false);
    }
  };

  const activeReferralCode = (codeFromUrl || referralCode).trim();

  const shareMessage = referralLink
    ? `Hi,\nI've been using Cashlet to save and invest towards my financial goals, and I thought it might be useful for you too.\nIf you're looking for a simple way to build your savings and investments, I'd definitely recommend giving it a try.\nSign up using my link:\n${referralLink}\nOr enter my referral code during signup:\n${activeReferralCode}\nLet me know if you need any help getting started.`
    : "";
  const smsMessage = referralLink
    ? `I've been using Cashlet to save and invest, and I think you'd like it too.\nSign up here:\n${referralLink}\nReferral Code: ${activeReferralCode}`
    : "";
  const emailSubject = "I've been using Cashlet — thought you'd like it too";
  const isShareMessageAvailable = Boolean(shareMessage);

  const isPlatformSelected = (platform) =>
    platform.action === "copy"
      ? copied
      : !copied && selectedPlatform === platform.id;

  const handleCopy = async () => {
    if (!shareMessage || copied) return;
    try {
      await navigator.clipboard.writeText(shareMessage);
      setCopied(true);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const platforms = [
    {
      id: "whatsapp",
      name: "WhatsApp",
      icon: FaWhatsapp,
      color: "#25D366",
      shareUrl: `https://wa.me/?text=${encodeURIComponent(shareMessage)}`,
    },
    {
      id: "telegram",
      name: "Telegram",
      icon: FaTelegramPlane,
      color: "#26A5E4",
      shareUrl: `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(shareMessage)}`,
    },
    {
      id: "sms",
      name: "SMS",
      icon: FiMessageSquare,
      color: "#0EA5E9",
      shareUrl: `sms:?body=${encodeURIComponent(smsMessage)}`,
    },
    {
      id: "email",
      name: "Email",
      icon: FiMail,
      color: "#EA4335",
      shareUrl: `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(shareMessage)}`,
    },
    {
      id: "copy-link",
      name: "Copy Link",
      icon: FiCopy,
      color: "#4B5563",
      action: "copy",
    },
  ];

  const handlePlatformClick = async (platform) => {
    if (!isShareMessageAvailable) return;

    if (platform.action === "copy") {
      if (copied) return;
      setSelectedPlatform(null);
      await handleCopy();
      return;
    }

    setCopied(false);
    setSelectedPlatform(platform.id);

    try {
      if (platform?.shareUrl) {
        window.open(platform.shareUrl, "_blank", "noopener,noreferrer");
        return;
      } else if (navigator.share) {
        await navigator.share({
          title: emailSubject,
          text: shareMessage,
          url: referralLink,
        });
      } else {
        handleCopy();
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        console.error("Failed to share:", err);
        await handleCopy();
      }
    }
  };

  const faqs = [
    {
      q: "Who can I refer?",
      a: "You can refer anyone who hasn't previously had a Cashlet account. Your friends or family members must be new users to qualify for the referral reward.",
    },
    {
      q: "How do I know if my referral qualified?",
      a: "Once your friend signs up and saves KES 10,000 within 30 days, you'll receive a notification. You can also track all your referrals and their status in the 'Track invites' section.",
    },
    {
      q: "How much is the referral reward?",
      a: "Each qualified referral gives you a savings boost that increases how fast your savings grow over time. The more friends you invite, the faster your savings will grow.",
    },
    {
      q: "When will I receive my reward?",
      a: "Your reward becomes available to claim once your friend has saved at least KES 10,000 within 30 days of signing up. You'll be notified when it's ready.",
    },
    {
      q: "Where does my referral reward go?",
      a: "When you claim your reward, you'll choose which savings goal to add it to. The reward will be deposited directly into that goal.",
    },
    {
      q: "Is there a limit to how many people I can refer?",
      a: "There's no limit! You can refer as many friends as you'd like. Each qualified referral earns you a reward.",
    },
    {
      q: "What if my friend doesn't save KES 10,000?",
      a: "If your friend doesn't reach the KES 10,000 savings threshold within 30 days of signing up, the referral won't qualify for a reward. However, they can continue saving with Cashlet.",
    },
    {
      q: "Can I send reminders to my friends?",
      a: "Yes! In the 'Track invites' section, you can send friendly reminders to friends who have signed up but haven't completed their qualifying savings yet.",
    },
    {
      q: "How does the savings boost work?",
      a: "When your referral qualifies, part of your savings earns at a higher rate for a limited time. This is added gradually to your goal.",
    },
  ];

  const steps = [
    {
      icon: hasCodeFromUrl ? BsQrCodeScan : HiOutlineLink,
      title: "1. Get your link",
      description: hasCodeFromUrl
        ? "Your unique referral link is ready below"
        : "Scan your Cashlet card or enter your code below",
    },
    {
      icon: FiShare2,
      title: "2. Share anywhere",
      description:
        "Share your link through direct channels your contacts trust",
    },
    {
      icon: IoTrendingUp,
      title: "3. Earn together",
      description: "You get an interest boost.",
    },
  ];

  return (
    <>
      <div className="px-4 md:px-8 pt-8 md:pt-12 pb-6 text-center">
        <img
          src={logo}
          alt="Cashlet"
          className="h-8 md:h-10 w-auto mx-auto mb-12 md:mb-16"
        />
        <h1 className="landing-text normal-case text-3xl md:text-5xl lg:text-5xl mb-4 md:mb-6">
          Invite friends. Grow your wealth.
        </h1>
        <p className="text-muted text-base md:text-xl max-w-2xl mx-auto">
          Help your friends start building wealth with Cashlet.<br /> When a friend
          you invite starts saving and investing with Cashlet, <br /> you’ll unlock up
          to 3 months of extra growth on your money.
        </p>
      </div>

      <div className="px-4 md:px-8 py-8 md:py-6">
        <div className="max-w-2xl mx-auto">
          {!hasCodeFromUrl && !referralLink && (
            <div className="mb-10">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (referralCode.trim()) handleGetLink();
                }}
                className="flex flex-col sm:flex-row gap-3"
              >
                <div className="flex-1 relative">
                  <HiOutlineUser className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Enter your referral code"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value)}
                    className="w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl text-base focus:outline-none focus:border-green-400 focus:ring-1 focus:ring-green-400"
                  />
                </div>
                <button
                  type="submit"
                  disabled={!referralCode.trim()}
                  className="btn-primary px-8 py-4 font-semibold text-base disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  Get My Link
                </button>
              </form>
              <p className="text-xs text-gray-400 mt-3 text-center sm:text-left">
                Find this code on your Cashlet card or in the app under More →
                Invite Friends.
              </p>
            </div>
          )}

          {referralLink && (
            <>
              {/* <div className="referral-link-box flex items-center justify-between p-4 rounded-xl mb-6">
                <span className="font-semibold text-gray-800 text-lg truncate">
                  {CHOTTU_REFERRAL_DISPLAY_URL}
                </span>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors flex-shrink-0 ml-4"
                >
                  {copied ? (
                    <FiCheck className="w-4 h-4 text-green-500" />
                  ) : (
                    <FiCopy className="w-4 h-4 text-gray-600" />
                  )}
                  <span className="text-sm font-medium">
                    {copied ? "Copied!" : "Copy"}
                  </span>
                </button>
              </div> */}
              <div className="mb-10">
                <p className="text-sm font-semibold text-gray-500 mb-10">
                  Choose how you’d like to share your referral
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                  {platforms.map((platform) => (
                    <button
                      key={platform.id}
                      onClick={() => handlePlatformClick(platform)}
                      className={`platform-btn flex flex-col items-center gap-2 p-2 md:p-3 rounded-xl border transition-all ${
                        isPlatformSelected(platform)
                          ? "border-green-400 bg-green-50 ring-1 ring-green-400"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                    >
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${platform.color}15` }}
                      >
                        {platform.action === "copy" && copied ? (
                          <FiCheck
                            className="w-5 h-5"
                            style={{ color: platform.color }}
                          />
                        ) : (
                          <platform.icon
                            className="w-5 h-5"
                            style={{ color: platform.color }}
                          />
                        )}
                      </div>
                      <span className="text-xs text-gray-600 font-medium">
                        {platform.action === "copy" && copied
                          ? "Copied!"
                          : platform.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Message Preview */}
              <div className="mb-10">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-6">
                  Message Preview:
                </p>
                <div
                  className="bg-gray-50 rounded-xl p-4 border border-gray-200"
                  onCopy={(event) => {
                    if (!shareMessage) return;
                    event.preventDefault();
                    event.clipboardData.setData("text/plain", shareMessage);
                  }}
                >
                  <p className="text-sm text-gray-600 whitespace-pre-line">
                    Hi,{"\n"}
                    I've been using Cashlet to save and invest towards my
                    financial goals, and I thought it might be useful for you
                    too.{"\n"}
                    If you're looking for a simple way to build your savings and
                    investments, I'd definitely recommend giving it a try.{"\n"}
                    Sign up using my link:{"\n"}
                    <span className="text-custom-green font-medium no-underline">
                      {CHOTTU_REFERRAL_DISPLAY_URL}
                    </span>
                    {"\n"}
                    Or enter my referral code during signup:{"\n"}
                    {activeReferralCode}
                    {"\n"}
                    Let me know if you need any help getting started.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* <div className="bg-gray-50 px-4 md:px-8 py-8 md:py-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {steps.map((step, index) => (
              <div
                key={index}
                className="flex items-start gap-4 bg-white rounded-2xl p-6 border border-gray-100"
              >
                <div className="w-14 h-14 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
                  <step.icon className="w-7 h-7 text-custom-green" />
                </div>
                <div>
                  <h3 className="font-bold text-lg text-gray-900 mb-1">
                    {step.title}
                  </h3>
                  <p className="text-sm text-gray-500">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div> */}

      {/* <div className="px-4 md:px-8 py-12 md:py-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="landing-subtitle text-2xl md:text-4xl text-center mb-10">
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() =>
                    setExpandedFaq(expandedFaq === index ? null : index)
                  }
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">
                    {faq.q}
                  </span>
                  {expandedFaq === index ? (
                    <FiChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  ) : (
                    <FiChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                  )}
                </button>
                {expandedFaq === index && (
                  <div className="px-5 pb-5">
                    <p className="text-gray-600">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div> */}
    </>
  );
};

export default ReferralPage;
